import { useEffect, useRef, useState } from "react";

import s1 from "./assets/Maximize.svg";
import s2 from "./assets/Move.svg";
import s3 from "./assets/Boost.svg";
import s4 from "./assets/Achieve.svg";
import s5 from "./assets/Gain.svg";
import "./Workforce.css";

const CARD_DATA = [
  {
    id: "labor-optimization",
    title: "Maximize Your Largest Investment: Labor",
    description:
      "Turn your workforce into a strategic asset by matching staff hours to real-time demand, cutting overstaffing, avoiding understaffing, and boosting overall profitability.",
    image: s1,
  },
  {
    id: "predictive-planning",
    title: "Move from Guessing to Knowing",
    description:
      "Replace guesswork with AI-powered scheduling: leveraging historical data, our system predicts demand, calculates precise labor needs, reduces waste and risk, and improves planning accuracy.",
    image: s2,
  },
  {
    id: "employee-retention",
    title: "Boost Morale and Retain Top Talent",
    description:
      "Prevent burnout and turnover by automatically assigning tasks based on skills, capacity, and rules, creating balanced, empowered teams that stay engaged, productive, and loyal.",
    image: s3,
  },
  {
    id: "operational-agility",
    title: "Achieve Unmatched Operational",
    description:
      "Handle unexpected changes effortlessly: our system dynamically reallocates workloads in real-time, managing rushes, call-outs, or new priorities quickly, ensuring smooth operations with minimal disruption.",
    image: s4,
  },
  {
    id: "real-time-visibility",
    title: "Gain Clear, Actionable Visibility",
    description:
      "Unlock your team’s productivity with a real-time dashboard displaying workload, task progress, and adherence, enabling managers to make smarter, strategic business decisions.",
    image: s5,
  },
];

const WorkforceIntelligence = () => {
  const cardsRef = useRef([]);
  const ticking = useRef(false);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = Number(entry.target.getAttribute("data-index"));
            setActiveIndex(index);
          }
        });
      },
      { threshold: 0.6 },
    );

    cardsRef.current.forEach((card) => card && observer.observe(card));
    return () => observer.disconnect();
  }, []);

  const updateActiveIndex = () => {
    const items = cardsRef.current;
    if (!items || items.length === 0) return;

    const centerY = window.innerHeight / 2;
    let closestIndex = 0;
    let minDistance = Infinity;

    items.forEach((el, index) => {
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const distance = Math.abs(rect.top + rect.height / 2 - centerY);

      if (distance < minDistance) {
        minDistance = distance;
        closestIndex = index;
      }
    });

    setActiveIndex((prev) => (prev !== closestIndex ? closestIndex : prev));
  };

  useEffect(() => {
    const onScroll = () => {
      if (!ticking.current) {
        ticking.current = true;
        requestAnimationFrame(() => {
          updateActiveIndex();
          ticking.current = false;
        });
      }
    };

    updateActiveIndex();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  useEffect(() => {
    cardsRef.current = cardsRef.current.slice(0, CARD_DATA.length);
  }, [CARD_DATA.length]);

  return (
    <section className="bg-[#F7F9FA] w-full flex flex-col lg:flex-row items-start justify-between gap-10 sm:gap-12 lg:gap-16 px-5 sm:px-8 md:px-14 lg:px-20 xl:px-28 py-10 sm:py-14 md:py-18 lg:py-20">
      <div className="w-full lg:w-1/2 xl:w-[45%] sticky lg:top-[100px] self-start text-center lg:text-left">
        <h2 className="font-epilogue font-semibold text-[#1A1A1A] text-[22px] sm:text-[28px] md:text-[34px] lg:text-[42px] leading-tight mb-4">
          Intelligent Workforce Scheduler Boosts Efficiency and Growth
        </h2>

        <p className="text-[#6B7280] font-epilogue text-[14px] sm:text-[15px] md:text-[16px] leading-relaxed mb-6 max-w-[600px] mx-auto lg:mx-0">
          An intelligent Workforce Scheduler turns reactive task juggling into
          proactive, efficient team management.
        </p>

        <div className="hidden lg:flex items-center gap-2 mt-4">
          {CARD_DATA.map((_, index) => (
            <div
              key={index}
              className={`h-[6px] w-[22px] rounded-md transition-colors duration-300 ${
                activeIndex === index ? "bg-[#1E72BB]" : "bg-[#D1D5DB]"
              }`}
            />
          ))}
          <span className="text-sm text-[#1A1A1A] ml-2">
            {activeIndex + 1}/{CARD_DATA.length}
          </span>
        </div>
      </div>

      <div className="flex-1 w-full flex justify-center lg:justify-start">
        <div className="flex flex-col gap-6 sm:gap-8 w-full max-w-[550px]">
          {CARD_DATA.map((card, index) => {
            const isActive = activeIndex === index;
            const isLast = index === CARD_DATA.length - 1;
            return (
              <div
                key={index}
                data-index={index}
                ref={(el) => (cardsRef.current[index] = el)}
                className={`bg-white  rounded-2xl shadow-sm md:shadow-md p-5 sm:p-6 md:p-7 transition-transform duration-500 ${
                  isLast ? "relative" : "lg:sticky lg:top-[120px]"
                } ${
                  isActive
                    ? "translate-y-0 scale-100"
                    : "translate-y-2 scale-[0.997]"
                }`}
              >
                <img
                  src={card.image}
                  alt={card.title}
                  className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 mb-3 mx-auto lg:mx-0"
                />
                <h3 className="font-epilogue font-semibold text-[#1A1A1A] text-[16px] sm:text-[18px] md:text-[20px] mb-2 leading-snug text-center lg:text-left">
                  {card.title}
                </h3>
                <p className="text-[#4B5563] font-epilogue text-[13px] sm:text-[14px] md:text-[15px] leading-relaxed text-center lg:text-left">
                  {card.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default WorkforceIntelligence;
